import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowUp, ArrowDown, Check, ChevronLeft, ChevronRight, Plus, MapPin, Camera, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { PageHeader } from "../components/ModulePage";
import { Button } from "../components/ui/button";

const L = (i) => String.fromCharCode(65 + (i % 26));
const move = (arr, i, d) => {
  const a = [...arr];
  [a[i], a[i + d]] = [a[i + d], a[i]];
  return a;
};

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex min-h-[40px] items-center gap-1.5 rounded-md border px-3 py-1.5 text-left text-sm transition-colors ${on ? "border-rose-500 bg-rose-500/15 text-rose-50" : "border-slate-700 text-slate-500 line-through"}`}>
    {on && <Check className="h-3.5 w-3.5 shrink-0" />}{children}
  </button>
);

const AddQuestion = ({ onAdd, testid }) => {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onAdd(v.trim()); setV(""); } };
  return (
    <div className="flex gap-2">
      <input className="field h-10" placeholder="Add your own question..." value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} data-testid={testid} />
      <Button type="button" variant="outline" className="h-10 border-slate-700 bg-transparent" onClick={add} data-testid={`${testid}-btn`}><Plus className="h-4 w-4" /></Button>
    </div>
  );
};

const slugq = (q) => q.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const QuestionPicker = ({ defaults, selected, onChange, photos = [], onPhotos, testid }) => {
  const all = [...defaults, ...selected.filter((q) => !defaults.includes(q))];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {all.map((q) => (
          <div key={q} className="flex items-stretch">
            <Chip on={selected.includes(q)} onClick={() => onChange(selected.includes(q) ? selected.filter((x) => x !== q) : [...selected, q])} testid={`${testid}-q-${slugq(q)}`}>{q}</Chip>
            {selected.includes(q) && onPhotos && (
              <button type="button" title="Photo always required" onClick={() => onPhotos(photos.includes(q) ? photos.filter((x) => x !== q) : [...photos, q])} data-testid={`${testid}-photo-${slugq(q)}`}
                className={`ml-1 flex w-10 items-center justify-center rounded-md border ${photos.includes(q) ? "border-amber-400 bg-amber-500/20 text-amber-200" : "border-slate-700 text-slate-500"}`}>
                <Camera className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {onPhotos && <p className="text-[11px] text-slate-500">Tap the camera next to a question to make a photo mandatory, even when the answer is OK.</p>}
      <AddQuestion onAdd={(q) => !selected.includes(q) && onChange([...selected, q])} testid={`${testid}-add`} />
    </div>
  );
};

const Basics = ({ p, set }) => (
  <div className="panel space-y-4 p-5" data-testid="builder-basics">
    <label className="block space-y-1.5"><span className="eyebrow">1. What is this pathway called? *</span>
      <input className="field" placeholder="e.g. Anstar Factory Daily Safety Walk" value={p.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="builder-name" />
    </label>
    <label className="block space-y-1.5"><span className="eyebrow">2. Describe the route (optional)</span>
      <input className="field" placeholder="e.g. Start at reception, finish at the boiler house" value={p.description || ""} onChange={(e) => set({ description: e.target.value })} data-testid="builder-description" />
    </label>
    <div className="space-y-1.5"><span className="eyebrow">3. How often must it be walked?</span>
      <div className="flex flex-wrap gap-2">
        {[[1, "Daily"], [7, "Weekly"], [14, "Fortnightly"], [30, "Monthly"], [91, "Quarterly"]].map(([d, l]) => (
          <Chip key={d} on={Number(p.frequency_days) === d} onClick={() => set({ frequency_days: d })} testid={`builder-freq-${d}`}>{l}</Chip>
        ))}
        <input type="number" className="field h-10 w-28" value={p.frequency_days || ""} onChange={(e) => set({ frequency_days: Number(e.target.value) })} data-testid="builder-freq-custom" />
      </div>
    </div>
  </div>
);

const RouteStep = ({ p, data, toggleArea, setSteps, addArea }) => {
  const [name, setName] = useState("");
  const name0 = Object.fromEntries(data.areas.map((a) => [a.area.id, a.area.name]));
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="builder-route">
      <div className="panel space-y-3 p-5">
        <p className="eyebrow">4. Tap the areas to include, in walking order</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {data.areas.map(({ area, assets }) => {
            const i = p.steps.indexOf(area.id);
            return (
              <button key={area.id} type="button" onClick={() => toggleArea(area.id)} data-testid={`builder-area-${area.code}`}
                className={`flex items-center gap-3 rounded-md border p-3 text-left ${i >= 0 ? "border-rose-500 bg-rose-500/10" : "border-slate-700 hover:border-slate-500"}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-mono text-sm ${i >= 0 ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-500"}`}>{i >= 0 ? L(i) : "+"}</span>
                <span><span className="block text-sm font-semibold">{area.name}</span><span className="text-xs text-slate-400">{area.code} - {assets.length} asset(s)</span></span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 pt-2">
          <input className="field h-10" placeholder="Area missing? Type a name to create it" value={name} onChange={(e) => setName(e.target.value)} data-testid="builder-new-area-name" />
          <Button type="button" variant="outline" className="h-10 border-slate-700 bg-transparent" disabled={!name.trim()} onClick={async () => { await addArea(name.trim()); setName(""); }} data-testid="builder-new-area-btn">Create</Button>
        </div>
      </div>
      <div className="panel space-y-2 p-5">
        <p className="eyebrow">Your route - start to finish</p>
        {p.steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2 rounded border border-slate-800 px-3 py-2 text-sm" data-testid={`builder-route-step-${i}`}>
            <span className="font-mono text-rose-300">{L(i)}</span><span className="flex-1">{name0[s]}</span>
            <button type="button" disabled={i === 0} onClick={() => setSteps(move(p.steps, i, -1))} className="p-1 disabled:opacity-30" data-testid={`builder-route-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" disabled={i === p.steps.length - 1} onClick={() => setSteps(move(p.steps, i, 1))} className="p-1 disabled:opacity-30" data-testid={`builder-route-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
          </div>
        ))}
        {!p.steps.length && <p className="text-sm text-slate-500">No areas selected yet.</p>}
      </div>
    </div>
  );
};

const AreaConfig = ({ idx, entry, cfg, setCfg, areaDefaults, areaPhotoDefaults }) => {
  const { area, assets } = entry;
  const byId = Object.fromEntries(assets.map((a) => [a.id, a]));
  const included = cfg.asset_ids.filter((id) => byId[id]);
  const excluded = assets.filter((a) => !included.includes(a.id));
  const qs = (a) => cfg.asset_questions[a.id] || a.questions;
  return (
    <div className="space-y-4" data-testid={`builder-config-${area.code}`}>
      <div className="panel p-4">
        <p className="eyebrow flex items-center gap-1 text-rose-300"><MapPin className="h-3 w-3" />Area {L(idx)} - {area.code}</p>
        <h2 className="font-display text-2xl font-bold uppercase">{area.name}</h2>
        <p className="text-sm text-slate-400">5. Which assets will the auditor check here, in what order, and what should they be asked?</p>
      </div>
      {included.map((id, i) => {
        const a = byId[id];
        return (
          <div key={id} className="panel space-y-3 p-4" data-testid={`builder-asset-${a.asset_code}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-cyan-300">{i + 1}. {a.asset_code}</span>
              <span className="flex-1 font-semibold">{a.name} <span className="text-xs text-slate-500">{a.type}</span></span>
              <button type="button" disabled={i === 0} onClick={() => setCfg({ asset_ids: move(included, i, -1) })} className="p-1 disabled:opacity-30" data-testid={`builder-asset-up-${a.asset_code}`}><ArrowUp className="h-4 w-4" /></button>
              <button type="button" disabled={i === included.length - 1} onClick={() => setCfg({ asset_ids: move(included, i, 1) })} className="p-1 disabled:opacity-30" data-testid={`builder-asset-down-${a.asset_code}`}><ArrowDown className="h-4 w-4" /></button>
              <Button type="button" size="sm" variant="ghost" className="text-red-300" onClick={() => setCfg({ asset_ids: included.filter((x) => x !== id) })} data-testid={`builder-asset-remove-${a.asset_code}`}>Skip</Button>
            </div>
            <QuestionPicker defaults={a.questions} selected={qs(a)} onChange={(list) => setCfg({ asset_questions: { ...cfg.asset_questions, [id]: list } })}
              photos={cfg.asset_photo?.[id] ?? a.photo_questions ?? []} onPhotos={(list) => setCfg({ asset_photo: { ...(cfg.asset_photo || {}), [id]: list } })} testid={`builder-asset-${a.asset_code}`} />
            {!qs(a).length && <p className="text-xs text-amber-300">No questions selected - the standard questions will be used.</p>}
          </div>
        );
      })}
      {excluded.length > 0 && (
        <div className="panel p-4">
          <p className="eyebrow mb-2">Not on this walk - tap to include</p>
          <div className="flex flex-wrap gap-2">
            {excluded.map((a) => <Chip key={a.id} on={false} onClick={() => setCfg({ asset_ids: [...included, a.id] })} testid={`builder-asset-include-${a.asset_code}`}>{a.asset_code} {a.name}</Chip>)}
          </div>
        </div>
      )}
      {!assets.length && <p className="panel p-4 text-sm text-slate-500">No assets in this area yet. Set the "Area" field on assets in any register to add them.</p>}
      <div className="panel space-y-3 p-4">
        <p className="eyebrow">6. General questions for this area</p>
        <QuestionPicker defaults={areaDefaults} selected={cfg.area_questions} onChange={(list) => setCfg({ area_questions: list })}
          photos={cfg.area_photo ?? areaPhotoDefaults} onPhotos={(list) => setCfg({ area_photo: list })} testid={`builder-area-${area.code}-general`} />
      </div>
    </div>
  );
};

const Review = ({ p, data }) => {
  const byArea = Object.fromEntries(data.areas.map((e) => [e.area.id, e]));
  let total = 0;
  const rows = p.steps.map((s, i) => {
    const e = byArea[s];
    const c = p.config[s];
    const assets = c.asset_ids.map((id) => e.assets.find((a) => a.id === id)).filter(Boolean);
    const n = assets.reduce((t, a) => t + (c.asset_questions[a.id] || a.questions).length, 0) + c.area_questions.length;
    total += n;
    return (
      <div key={s} className="border-b border-slate-800 py-3 last:border-0">
        <p className="font-semibold"><span className="mr-2 font-mono text-rose-300">{L(i)}</span>{e.area.name} <span className="text-xs text-slate-500">{n} questions</span></p>
        <p className="mt-1 text-xs text-slate-400">{assets.map((a) => a.asset_code).join(" → ") || "No assets"}{c.area_questions.length ? " → general area checks" : ""}</p>
      </div>
    );
  });
  return (
    <div className="panel p-5" data-testid="builder-review">
      <p className="eyebrow">7. Review</p>
      <h2 className="font-display text-2xl font-bold uppercase">{p.name}</h2>
      <p className="text-sm text-slate-400">Every {p.frequency_days} day(s) - {p.steps.length} areas - <span data-testid="builder-total-questions">{total}</span> questions</p>
      <div className="mt-3">{rows}</div>
    </div>
  );
};

export default function PathwayBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [p, setP] = useState({ name: "", description: "", frequency_days: 1, steps: [], config: {} });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(!id);

  const loadData = useCallback(() => api.get("/pathway-builder").then((r) => { setData(r.data); return r.data; }), []);
  useEffect(() => {
    loadData().then((d) => {
      if (!id) return;
      api.get("/pathways").then((r) => {
        const ex = r.data.find((x) => x.id === id);
        setReady(true);
        if (!ex) return;
        const config = {};
        ex.steps.forEach((s) => {
          const e = d.areas.find((a) => a.area.id === s);
          config[s] = ex.config?.[s] || { asset_ids: (e?.assets || []).map((a) => a.id), asset_questions: {}, asset_photo: {}, area_photo: null, area_questions: d.area_questions };
        });
        setP({ ...ex, config });
      });
    });
  }, [id, loadData]);

  if (!data || !ready) return <p className="text-slate-500" data-testid="builder-loading">Loading areas and assets...</p>;
  const set = (patch) => setP((x) => ({ ...x, ...patch }));
  const defaultCfg = (aid) => ({ asset_ids: (data.areas.find((a) => a.area.id === aid)?.assets || []).map((a) => a.id), asset_questions: {}, asset_photo: {}, area_photo: null, area_questions: data.area_questions });
  const toggleArea = (aid) => setP((x) => x.steps.includes(aid)
    ? { ...x, steps: x.steps.filter((s) => s !== aid) }
    : { ...x, steps: [...x.steps, aid], config: { ...x.config, [aid]: x.config[aid] || defaultCfg(aid) } });
  const addArea = async (name) => {
    try {
      const r = await api.post("/areas", { name });
      await loadData();
      setP((x) => ({ ...x, steps: [...x.steps, r.data.id], config: { ...x.config, [r.data.id]: { asset_ids: [], asset_questions: {}, asset_photo: {}, area_photo: null, area_questions: data.area_questions } } }));
      toast.success(`Area ${r.data.name} created and added`);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const total = 2 + p.steps.length + 1;
  const titles = ["Basics", "Route", ...p.steps.map((s, i) => `${L(i)}. ${data.areas.find((a) => a.area.id === s)?.area.name}`), "Review"];
  const canNext = step === 0 ? !!p.name.trim() && p.frequency_days > 0 : step === 1 ? p.steps.length > 0 : true;
  const save = async () => {
    setBusy(true);
    const body = { name: p.name, description: p.description, frequency_days: p.frequency_days, steps: p.steps, config: Object.fromEntries(p.steps.map((s) => [s, p.config[s]])) };
    try {
      if (id) await api.put(`/pathways/${id}`, body);
      else await api.post("/pathways", body);
      toast.success("Pathway saved");
      nav("/audits");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const areaIdx = step - 2;

  return (
    <div className="mx-auto max-w-4xl" data-testid="pathway-builder">
      <PageHeader eyebrow="Guided pathway builder" title={id ? "Edit pathway" : "Build a pathway"} description="Answer each step: name the walk, pick the areas in order, then choose the assets and questions for each area.">
        <Button variant="outline" className="tap border-slate-700 bg-transparent" onClick={() => nav("/question-library")} data-testid="builder-question-library-btn"><ListChecks className="mr-1 h-4 w-4" />Question library</Button>
      </PageHeader>
      <div className="mb-5 flex gap-1 overflow-x-auto pb-1" data-testid="builder-progress">
        {titles.map((t, i) => (
          <button key={i} type="button" onClick={() => (i <= 1 || p.steps.length) && (i < 2 || p.name) && setStep(i)} data-testid={`builder-step-${i}`}
            className={`shrink-0 rounded-md border px-3 py-2 text-xs ${i === step ? "border-rose-500 bg-rose-600 text-white" : i < step ? "border-emerald-600 bg-emerald-600/15 text-emerald-200" : "border-slate-700 text-slate-400"}`}>{t}</button>
        ))}
      </div>
      {step === 0 && <Basics p={p} set={set} />}
      {step === 1 && <RouteStep p={p} data={data} toggleArea={toggleArea} setSteps={(steps) => set({ steps })} addArea={addArea} />}
      {areaIdx >= 0 && areaIdx < p.steps.length && (
        <AreaConfig key={p.steps[areaIdx]} idx={areaIdx} entry={data.areas.find((a) => a.area.id === p.steps[areaIdx])} cfg={p.config[p.steps[areaIdx]]}
          areaDefaults={data.area_questions} areaPhotoDefaults={data.area_photo_questions || []} setCfg={(patch) => setP((x) => ({ ...x, config: { ...x.config, [x.steps[areaIdx]]: { ...x.config[x.steps[areaIdx]], ...patch } } }))} />
      )}
      {step === total - 1 && <Review p={p} data={data} />}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} className="h-12 border-slate-700 bg-transparent" data-testid="builder-back-btn"><ChevronLeft className="mr-1 h-4 w-4" />Back</Button>
        {step < total - 1 ? (
          <Button disabled={!canNext} onClick={() => { setStep(step + 1); window.scrollTo(0, 0); }} className="h-12 bg-rose-600 hover:bg-rose-700" data-testid="builder-next-btn">Next: {titles[step + 1]}<ChevronRight className="ml-1 h-4 w-4" /></Button>
        ) : (
          <Button disabled={busy} onClick={save} className="h-12 bg-emerald-600 hover:bg-emerald-700" data-testid="builder-save-btn">{busy ? "Saving..." : "Save pathway"}</Button>
        )}
      </div>
    </div>
  );
}
